import { Button, Col, Row, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useAntTheme } from "hooks/useAntTheme";
import styled from "styled-components";
import { radius } from "@libertify/ui";

const StyledMenuButton = styled(Button)<{ $disabled?: boolean }>`
  height: auto;
  padding: 12px 16px;
  border-radius: ${radius.box}px;
  border: ${({ theme }) => theme.cardBorder};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  background-color: ${({ theme, $disabled }) =>
    $disabled ? "#ffffff14" : theme.colorCardRedesign};
  box-shadow: ${({ theme }) => theme.cardBoxShadow};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  /* Override Ant Design button styles */
  &:not(:disabled, .ant-btn-disabled) {
    &:hover,
    &:focus {
      border-color: ${({ theme, $disabled }) =>
        $disabled ? undefined : theme.colorPrimary};
      background-color: ${({ theme, $disabled }) =>
        $disabled ? "#ffffff14" : theme.colorCardRedesign};

      .icon-wrapper {
        color: ${({ theme, $disabled }) =>
          $disabled ? undefined : theme.colorPrimary};
      }
    }
  }
`;

const StyledIconWrapper = styled.div<{ $disabled?: boolean }>`
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colorTextDisabled : "#666666"};
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
`;

const StyledText = styled(Typography.Text)<{ $disabled?: boolean }>`
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colorTextDisabled : theme.colorTextBaseRedesign};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  white-space: normal;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  text-align: left;
`;

interface ProjectActionCardProps {
  route?: string;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}

export const ProjectActionCard: React.FC<ProjectActionCardProps> = ({
  route,
  icon,
  label,
  disabled,
  onClick,
}) => {
  const { theme } = useAntTheme();
  const navigate = useNavigate();

  const handleItemClick = () => {
    if (onClick) {
      onClick();
    } else if (route) {
      navigate(route);
    }
  };

  return (
    <StyledMenuButton
      disabled={disabled}
      $disabled={disabled}
      onClick={handleItemClick}
      aria-label={label}
      aria-disabled={disabled}
    >
      <Row align="middle" justify="space-between" style={{ width: "100%" }}>
        <Col
          flex="auto"
          style={{ display: "flex", alignItems: "center", gap: "18px" }}
        >
          <StyledIconWrapper className="icon-wrapper" $disabled={disabled}>
            {icon}
          </StyledIconWrapper>
          <StyledText $disabled={disabled} theme={theme}>
            {label}
          </StyledText>
        </Col>
      </Row>
    </StyledMenuButton>
  );
};
